/*
 * Copyright Mimic Networks, Inc. 2025.
 */
import { PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfigRevision, NodeConfig } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { GuardedButton } from '@/components/GuardedButton';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedNodeConfigsQuery } from '@/hooks/query/usePaginatedNodeConfigsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ConfigFilter, ConfigsHeader } from '@/pages/Configs/ConfigsHeader';
import { NodeConfigsList } from '@/pages/Configs/NodeConfigsList';
import { UploadNodeConfigModal } from '@/pages/Configs/UploadNodeConfigModal';
import { Container } from '@/primitives/Container';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export function ConfigsPage() {
  let configsQuery: ReturnType<typeof usePaginatedNodeConfigsQuery>;
  let configState: ConfigFilter;
  const selectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const { t } = useMimicTranslation('nodeConfigurations');

  const activeConfigsQuery = usePaginatedNodeConfigsQuery({ type: 'queryParams' });
  const archivedConfigsQuery = usePaginatedNodeConfigsQuery({
    type: 'queryParams',
    params: { filters: { isArchived: true } },
  });

  const activeCount = activeConfigsQuery.data?.meta.page.totalItems || 0;
  const archivedCount = archivedConfigsQuery.data?.meta.page.totalItems || 0;

  const nodeConfigPath = (nodeConfig: NodeConfig) => `/tenants/${selectedTenantID}/node-configs/${nodeConfig.id}`;
  const getNodeConfigurationRevisionPath = (nodeConfigRevision: ConfigRevision) =>
    `/tenants/${selectedTenantID}/node-configs/${nodeConfigRevision.nodeConfigId}/revisions/${nodeConfigRevision.revisionNumber}`;

  const handleConfigState = (state: ConfigFilter) => {
    setSearchParams((prev) => {
      prev.set('configState', state);
      return prev;
    });
  };

  switch (searchParams.get('configState')) {
    case ConfigFilter.ARCHIVED:
      configsQuery = archivedConfigsQuery;
      configState = ConfigFilter.ARCHIVED;
      break;
    default:
      configsQuery = activeConfigsQuery;
      configState = ConfigFilter.ACTIVE;
      break;
  }

  return (
    <ContentContainer width="100%">
      <Container>
        <ConfigsHeader
          setConfigState={handleConfigState}
          configState={configState}
          activeCount={activeCount}
          archivedCount={archivedCount}
          activeConfigsLoading={activeConfigsQuery.isLoading}
          archivedConfigsLoading={archivedConfigsQuery.isLoading}
        />
        <QueryWrapper queryResult={configsQuery}>
          {(configsData) => (
            <NodeConfigsList
              tags={[]}
              onParamsChange={configsQuery.updateQueryParams}
              configsData={configsData}
              getNodeConfigPath={nodeConfigPath}
              getNodeConfigurationRevisionPath={getNodeConfigurationRevisionPath}
              primaryAction={
                <GuardedButton
                  requiredRole="superadmin"
                  type="primary"
                  onClick={() => setUploadModalOpen(true)}
                  data-testid="upload-config-button"
                  style={{
                    height: '36px',
                  }}
                >
                  <PlusOutlined /> {t('uploadConfig')}
                </GuardedButton>
              }
            />
          )}
        </QueryWrapper>
      </Container>

      <UploadNodeConfigModal open={uploadModalOpen} onCancel={() => setUploadModalOpen(false)} mode="new" />
    </ContentContainer>
  );
}
